import { useCallback, useEffect, useState } from "react";
import Editor from "@monaco-editor/react";
import {
  api,
  type Assignment,
  type QuestionOption,
  type Rubric,
  type TestCase,
  type ValidationReport,
} from "../api.js";

/**
 * One question, edited where it sits.
 *
 * A lab sheet and an exam paper are both lists of questions, and writing one
 * used to mean leaving the list for a page of its own and finding your way
 * back. Everything a question needs -- statement, formats, marks, limits,
 * starter code, test cases or options -- is here, so a sheet of five is written
 * without navigating once.
 *
 * The question loads only when opened. A paper of ten would otherwise fetch ten
 * assignments, with their test cases, to show ten collapsed headers.
 */
export default function QuestionEditor({
  questionId,
  index,
  defaultOpen = false,
  onSummaryChange,
  actions,
}: {
  questionId: string;
  /** Position in the list, for the heading. */
  index: number;
  defaultOpen?: boolean;
  /** Called after a save that changes what the list shows. */
  onSummaryChange?: () => void;
  /** Reorder and delete controls, owned by whatever holds the list. */
  actions?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState<Assignment | null>(null);
  const [summary, setSummary] = useState<{
    title: string;
    kind: string;
    maxMarks: number;
    ready: boolean;
  } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [dirtyCases, setDirtyCases] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ assignment: Assignment }>(`/api/assignments/${questionId}`);
      setQ(r.assignment);
      setSummary({
        title: r.assignment.title,
        kind: r.assignment.kind,
        maxMarks: r.assignment.maxMarks,
        ready:
          r.assignment.kind === "MCQ"
            ? r.assignment.options.length > 0
            : r.assignment.testCases.length > 0,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the question.");
    }
  }, [questionId]);

  // The header needs a title and marks even while collapsed; the rest waits.
  useEffect(() => {
    void load();
  }, [load]);

  function patch(fields: Partial<Assignment>) {
    setQ((prev) => (prev ? { ...prev, ...fields } : prev));
    setDirty(true);
  }

  function updateTestCase(tcId: string, fields: Partial<TestCase>) {
    setQ((prev) =>
      prev
        ? { ...prev, testCases: prev.testCases.map((t) => (t.id === tcId ? { ...t, ...fields } : t)) }
        : prev
    );
    setDirtyCases((prev) => new Set(prev).add(tcId));
  }

  async function save() {
    if (!q) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      await api.patch(`/api/assignments/${q.id}`, {
        title: q.title,
        description: q.description,
        inputFormat: q.inputFormat,
        outputFormat: q.outputFormat,
        starterCode: q.starterCode,
        maxMarks: q.maxMarks,
        difficulty: q.difficulty ?? "",
        topic: q.topic ?? "",
        timeLimitMs: q.timeLimitMs,
        memoryLimitKb: q.memoryLimitKb,
      });
      for (const tc of q.testCases.filter((t) => dirtyCases.has(t.id))) {
        await api.patch(`/api/testcases/${tc.id}`, {
          name: tc.name,
          stdin: tc.stdin ?? "",
          expectedStdout: tc.expectedStdout ?? "",
          weight: tc.weight,
          hidden: tc.hidden,
          matchMode: tc.matchMode ?? "TRIMMED",
          orderIndex: tc.orderIndex,
        });
      }
      setDirty(false);
      setDirtyCases(new Set());
      setStatus("Saved.");
      await load();
      onSummaryChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function addTestCase(copyOf?: TestCase) {
    if (!q) return;
    try {
      const r = await api.post<{ testCase: TestCase }>(`/api/assignments/${q.id}/testcases`, {
        name: copyOf ? `${copyOf.name} (copy)` : `Test ${q.testCases.length + 1}`,
        stdin: copyOf?.stdin ?? "",
        expectedStdout: copyOf?.expectedStdout ?? "",
        weight: copyOf?.weight ?? 1,
        hidden: copyOf?.hidden ?? true,
        matchMode: copyOf?.matchMode ?? "TRIMMED",
        orderIndex: q.testCases.length,
      });
      setQ((prev) => (prev ? { ...prev, testCases: [...prev.testCases, r.testCase] } : prev));
      onSummaryChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add the test case.");
    }
  }

  async function moveTestCase(i: number, delta: number) {
    if (!q) return;
    const target = i + delta;
    if (target < 0 || target >= q.testCases.length) return;
    const reordered = [...q.testCases];
    [reordered[i], reordered[target]] = [reordered[target], reordered[i]];
    const renumbered = reordered.map((tc, n) => ({ ...tc, orderIndex: n }));
    setQ((prev) => (prev ? { ...prev, testCases: renumbered } : prev));
    try {
      await Promise.all(
        [renumbered[i], renumbered[target]].map((tc) =>
          api.patch(`/api/testcases/${tc.id}`, { orderIndex: tc.orderIndex })
        )
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not reorder the test cases.");
    }
  }

  async function deleteTestCase(tcId: string) {
    if (!q || !confirm("Delete this test case?")) return;
    try {
      await api.del(`/api/testcases/${tcId}`);
      setQ((prev) =>
        prev ? { ...prev, testCases: prev.testCases.filter((t) => t.id !== tcId) } : prev
      );
      onSummaryChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the test case.");
    }
  }

  const isMcq = summary?.kind === "MCQ";

  return (
    <div className="question">
      <div className="question-head" onClick={() => setOpen((v) => !v)}>
        <span className="muted">{index}.</span>
        <span className="name">
          {summary?.title ?? "…"}{" "}
          <span className="badge draft">{isMcq ? "multiple choice" : "programming"}</span>{" "}
          {summary && !summary.ready && (
            <span className="badge warn">{isMcq ? "needs options" : "needs test cases"}</span>
          )}
          {(dirty || dirtyCases.size > 0) && <span className="badge warn">unsaved</span>}
        </span>
        <span className="muted">{summary?.maxMarks ?? "—"} marks</span>
        <div className="row" onClick={(e) => e.stopPropagation()}>
          {actions}
          <button onClick={() => setOpen((v) => !v)}>{open ? "Close" : "Edit"}</button>
        </div>
      </div>

      {open && q && (
        <div className="question-body">
          {error && <div className="error-banner">{error}</div>}
          {status && <div className="notice">{status}</div>}

          <label>
            <span>Question title</span>
            <input value={q.title} onChange={(e) => patch({ title: e.target.value })} />
          </label>

          <label>
            <span>Problem statement (markdown)</span>
            <textarea
              rows={6}
              value={q.description}
              onChange={(e) => patch({ description: e.target.value })}
            />
          </label>

          {!isMcq && (
            <>
              <div className="grid-2">
                <label>
                  <span>Input format</span>
                  <textarea
                    rows={3}
                    value={q.inputFormat}
                    placeholder={"First line: n, the number of elements."}
                    onChange={(e) => patch({ inputFormat: e.target.value })}
                  />
                </label>
                <label>
                  <span>Output format</span>
                  <textarea
                    rows={3}
                    value={q.outputFormat}
                    placeholder={"A single integer, followed by a newline."}
                    onChange={(e) => patch({ outputFormat: e.target.value })}
                  />
                </label>
              </div>
              <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
                Output is compared against the expected text, so an extra prompt like{" "}
                <code>Enter a number:</code> fails every test.
              </p>
            </>
          )}

          <div className="grid-2">
            <label>
              <span>Marks</span>
              <input
                type="number"
                min={1}
                value={q.maxMarks}
                onChange={(e) => patch({ maxMarks: Number(e.target.value) })}
              />
            </label>
            <label>
              <span>Topic</span>
              <input
                value={q.topic ?? ""}
                onChange={(e) => patch({ topic: e.target.value })}
                placeholder="Loops, Arrays, Pointers…"
              />
            </label>
            <label>
              <span>Difficulty</span>
              <select
                value={q.difficulty ?? ""}
                onChange={(e) => patch({ difficulty: e.target.value as Assignment["difficulty"] })}
              >
                <option value="">Unrated</option>
                <option value="EASY">Easy</option>
                <option value="MEDIUM">Medium</option>
                <option value="HARD">Hard</option>
              </select>
            </label>
            {!isMcq && (
              <label>
                <span>Time limit per test (ms)</span>
                <input
                  type="number"
                  min={100}
                  step={100}
                  value={q.timeLimitMs}
                  onChange={(e) => patch({ timeLimitMs: Number(e.target.value) })}
                />
              </label>
            )}
          </div>

          {isMcq ? (
            <McqOptions
              assignmentId={q.id}
              options={q.options}
              onChange={(options) => {
                setQ((prev) => (prev ? { ...prev, options } : prev));
                onSummaryChange?.();
              }}
              onError={setError}
            />
          ) : (
            <>
              <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>STARTER CODE</div>
              <div style={{ height: 180, border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
                <Editor
                  language="c"
                  theme="vs-dark"
                  value={q.starterCode}
                  onChange={(v) => patch({ starterCode: v ?? "" })}
                  options={{ fontSize: 13, minimap: { enabled: false }, tabSize: 4, automaticLayout: true }}
                />
              </div>

              <TestCases
                testCases={q.testCases}
                dirtyCases={dirtyCases}
                onUpdate={updateTestCase}
                onAdd={addTestCase}
                onMove={moveTestCase}
                onDelete={deleteTestCase}
              />

              <Validator assignmentId={q.id} starterCode={q.starterCode} dirty={dirty} />

              <RubricEditor assignmentId={q.id} maxMarks={q.maxMarks} />
            </>
          )}

          <div className="row" style={{ marginTop: 12 }}>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save question"}
            </button>
            <button onClick={() => setOpen(false)}>Close</button>
            {(dirty || dirtyCases.size > 0) && (
              <span className="muted" style={{ fontSize: 13 }}>Unsaved changes</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Marking criteria for AI-assisted partial credit, and the cap on it.
 *
 * Folded away and off by default. A question with no criteria and a cap of 0 —
 * every question, until a teacher opens this — is one the feature skips
 * entirely: no model call, no suggested marks. Opting in is a deliberate act
 * per question, which is what makes "leave it off for the final exam" a
 * setting rather than a deployment decision.
 */
function RubricEditor({ assignmentId, maxMarks }: { assignmentId: string; maxMarks: number }) {
  const [open, setOpen] = useState(false);
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [rows, setRows] = useState<{ text: string; marks: number }[]>([]);
  const [cap, setCap] = useState(0);
  const [busy, setBusy] = useState<null | "draft" | "save">(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!open || rubric) return;
    api
      .get<Rubric>(`/api/assignments/${assignmentId}/rubric`)
      .then((r) => {
        setRubric(r);
        setRows(r.criteria.map((c) => ({ text: c.text, marks: c.marks })));
        setCap(r.aiCreditCapPercent);
      })
      .catch((e) => setError(e.message));
  }, [open, rubric, assignmentId]);

  async function draft() {
    setBusy("draft");
    setError(null);
    setStatus(null);
    try {
      const r = await api.post<{ criteria: { text: string; marks: number }[] }>(
        `/api/assignments/${assignmentId}/rubric/draft`,
        {}
      );
      // Replaces the editor's contents, not the saved rubric — nothing marks a
      // student until the teacher reads these and saves.
      setRows(r.criteria);
      setStatus("Drafted. Edit these, then save — nothing is used until you do.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not draft criteria.");
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    setBusy("save");
    setError(null);
    setStatus(null);
    try {
      const r = await api.put<{ criteria: Rubric["criteria"]; rubricVersion: number }>(
        `/api/assignments/${assignmentId}/rubric`,
        { criteria: rows.filter((c) => c.text.trim()), aiCreditCapPercent: cap }
      );
      setRubric((prev) => (prev ? { ...prev, criteria: r.criteria, rubricVersion: r.rubricVersion, aiCreditCapPercent: cap } : prev));
      setStatus(`Saved as rubric v${r.rubricVersion}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the rubric.");
    } finally {
      setBusy(null);
    }
  }

  const capMarks = Math.round((maxMarks * cap) / 100 * 100) / 100;

  return (
    <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
      <div className="between">
        <div>
          <span style={{ color: "var(--muted)", fontSize: 12 }}>PARTIAL CREDIT (AI-ASSISTED)</span>
          {rubric && rubric.aiCreditCapPercent > 0 && (
            <span className="badge pass" style={{ marginLeft: 8 }}>on</span>
          )}
        </div>
        <button onClick={() => setOpen((v) => !v)}>{open ? "Hide" : "Set up"}</button>
      </div>

      {open && !rubric && !error && <div className="muted" style={{ marginTop: 10 }}>Loading…</div>}
      {open && error && <div className="error-banner" style={{ marginTop: 10 }}>{error}</div>}

      {open && rubric && !rubric.enabled && (
        <div className="notice" style={{ marginTop: 10 }}>
          AI marking is not configured on this server. Ask an administrator to set
          <code> ANTHROPIC_API_KEY</code>.
        </div>
      )}

      {open && rubric && rubric.enabled && (
        <div style={{ marginTop: 10 }}>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Criteria describe what a good <em>approach</em> looks like, so a student whose code
            fails the tests can still earn marks for getting the method right — and be told which
            part they got right. Credit is only ever <strong>added</strong> to the marks the test
            cases awarded; it can never reduce a mark.
          </p>

          {rows.map((c, i) => (
            <div className="row" key={i} style={{ marginBottom: 6 }}>
              <input
                value={c.text}
                placeholder="e.g. Uses a loop to accumulate the total"
                onChange={(e) =>
                  setRows((prev) => prev.map((r, j) => (j === i ? { ...r, text: e.target.value } : r)))
                }
              />
              <input
                type="number"
                min={1}
                style={{ width: 90 }}
                value={c.marks}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r, j) => (j === i ? { ...r, marks: Number(e.target.value) || 1 } : r))
                  )
                }
              />
              <button className="danger" onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>
                Remove
              </button>
            </div>
          ))}

          <div className="row" style={{ marginTop: 8 }}>
            <button onClick={() => setRows((prev) => [...prev, { text: "", marks: 1 }])}>
              Add criterion
            </button>
            <button onClick={draft} disabled={busy !== null}>
              {busy === "draft" ? "Drafting…" : "Draft with AI"}
            </button>
          </div>

          <label className="row" style={{ marginTop: 14, width: "auto", alignItems: "center" }}>
            <span style={{ marginBottom: 0 }}>Most AI credit this question may award</span>
            <input
              type="number"
              min={0}
              max={100}
              style={{ width: 90 }}
              value={cap}
              onChange={(e) => setCap(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
            />
            <span className="muted" style={{ marginBottom: 0 }}>
              % — up to {capMarks} of {maxMarks} marks
            </span>
          </label>
          {/* Said plainly, because "0" is the setting a teacher wants on a final
              exam and it should not require reading the docs to discover. */}
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            {cap === 0
              ? "0% switches AI partial credit off for this question."
              : `A submission can gain at most ${capMarks} marks this way, and never more than reaches full marks.`}
          </div>

          {status && <div className="notice" style={{ marginTop: 10 }}>{status}</div>}
          <div className="row" style={{ marginTop: 10 }}>
            <button className="primary" onClick={save} disabled={busy !== null}>
              {busy === "save" ? "Saving…" : "Save rubric"}
            </button>
            {rubric.rubricVersion > 1 && (
              <span className="muted" style={{ fontSize: 12.5 }}>
                Currently v{rubric.rubricVersion}. Marks already given record the version they were
                judged against.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** The test cases of one programming question. */
function TestCases({
  testCases,
  dirtyCases,
  onUpdate,
  onAdd,
  onMove,
  onDelete,
}: {
  testCases: TestCase[];
  dirtyCases: Set<string>;
  onUpdate: (id: string, fields: Partial<TestCase>) => void;
  onAdd: (copyOf?: TestCase) => void;
  onMove: (index: number, delta: number) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div style={{ marginTop: 14 }}>
      <div className="between" style={{ marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>Test cases ({testCases.length})</strong>
        <button onClick={() => onAdd()}>Add test case</button>
      </div>

      {testCases.length === 0 && (
        <div className="muted" style={{ marginBottom: 10 }}>
          No test cases yet. Students cannot submit until at least one exists.
        </div>
      )}

      {testCases.map((tc, i) => (
        <div key={tc.id} className="card" style={{ background: "var(--bg)", marginBottom: 10 }}>
          <div className="row" style={{ marginBottom: 10, flexWrap: "wrap" }}>
            <input
              style={{ flex: 1, minWidth: 140 }}
              value={tc.name}
              onChange={(e) => onUpdate(tc.id, { name: e.target.value })}
            />
            <select
              style={{ width: 150 }}
              value={tc.matchMode ?? "TRIMMED"}
              onChange={(e) => onUpdate(tc.id, { matchMode: e.target.value })}
            >
              <option value="TRIMMED">Trimmed match</option>
              <option value="EXACT">Exact match</option>
              <option value="NUMERIC">Numeric tolerance</option>
            </select>
            <label className="row" style={{ marginBottom: 0, width: "auto", whiteSpace: "nowrap" }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={tc.hidden}
                onChange={(e) => onUpdate(tc.id, { hidden: e.target.checked })}
              />
              <span style={{ marginBottom: 0 }}>Hidden</span>
            </label>
            <input
              type="number"
              min={1}
              style={{ width: 70 }}
              title="Weight"
              value={tc.weight}
              onChange={(e) => onUpdate(tc.id, { weight: Number(e.target.value) })}
            />
            <button title="Move up" disabled={i === 0} onClick={() => onMove(i, -1)}>↑</button>
            <button
              title="Move down"
              disabled={i === testCases.length - 1}
              onClick={() => onMove(i, 1)}
            >
              ↓
            </button>
            <button title="Duplicate" onClick={() => onAdd(tc)}>Duplicate</button>
            <button className="danger" onClick={() => onDelete(tc.id)}>Delete</button>
            {dirtyCases.has(tc.id) && <span className="badge warn">edited</span>}
          </div>

          <div className="grid-2">
            <label style={{ marginBottom: 0 }}>
              <span>Input (stdin)</span>
              <textarea
                rows={3}
                value={tc.stdin ?? ""}
                onChange={(e) => onUpdate(tc.id, { stdin: e.target.value })}
              />
            </label>
            <label style={{ marginBottom: 0 }}>
              <span>Expected output</span>
              <textarea
                rows={3}
                value={tc.expectedStdout ?? ""}
                onChange={(e) => onUpdate(tc.id, { expectedStdout: e.target.value })}
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The answer key for a multiple-choice question.
 *
 * Options save as you edit them rather than waiting for the page's Save: each
 * is one small row, and a half-written key is a worse thing to leave behind
 * than a half-written statement.
 */
export function McqOptions({
  assignmentId,
  options,
  onChange,
  onError,
}: {
  assignmentId: string;
  options: QuestionOption[];
  onChange: (options: QuestionOption[]) => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const correctCount = options.filter((o) => o.correct).length;

  async function add() {
    setBusy(true);
    try {
      const r = await api.post<{ option: QuestionOption }>(
        `/api/assignments/${assignmentId}/options`,
        { text: `Option ${options.length + 1}`, correct: false, orderIndex: options.length }
      );
      onChange([...options, r.option]);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not add the option.");
    } finally {
      setBusy(false);
    }
  }

  async function update(id: string, fields: Partial<QuestionOption>) {
    onChange(options.map((o) => (o.id === id ? { ...o, ...fields } : o)));
    try {
      await api.patch(`/api/options/${id}`, fields);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not save the option.");
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this option?")) return;
    try {
      await api.del(`/api/options/${id}`);
      onChange(options.filter((o) => o.id !== id));
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not delete the option.");
    }
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div className="between" style={{ marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>Options ({options.length})</strong>
        <button onClick={add} disabled={busy}>Add option</button>
      </div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Tick every option that is correct. Marking is all or nothing: full marks for choosing
        exactly the ticked set, zero otherwise.
        {correctCount > 1 && " Students see checkboxes and must choose all of them."}
      </p>

      {options.length === 0 && (
        <div className="muted">No options yet. Students cannot answer until you add some.</div>
      )}

      {options.map((o) => (
        <div className="row" key={o.id} style={{ marginBottom: 8 }}>
          <label
            className="row"
            style={{ marginBottom: 0, width: "auto", whiteSpace: "nowrap" }}
            title="Correct answer"
          >
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={!!o.correct}
              onChange={(e) => update(o.id, { correct: e.target.checked })}
            />
            <span style={{ marginBottom: 0 }}>Correct</span>
          </label>
          <input
            style={{ flex: 1 }}
            value={o.text}
            onChange={(e) => onChange(options.map((x) => (x.id === o.id ? { ...x, text: e.target.value } : x)))}
            onBlur={(e) => update(o.id, { text: e.target.value })}
          />
          <button className="danger" onClick={() => remove(o.id)}>Delete</button>
        </div>
      ))}
    </div>
  );
}

/**
 * Runs a reference solution against every test case and reports disagreements.
 *
 * Expected outputs are typed by hand, and a wrong one is invisible: it fails
 * every student identically, and the first sign is a class that all scored the
 * same mark.
 */
export function Validator({
  assignmentId,
  starterCode,
  dirty,
}: {
  assignmentId: string;
  starterCode: string;
  dirty: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function check() {
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      setReport(await api.post<ValidationReport>(`/api/assignments/${assignmentId}/validate`, { code }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not run the check.");
    } finally {
      setBusy(false);
    }
  }

  const disagreements = report?.results.filter((r) => !r.agrees) ?? [];

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
      <div className="between">
        <div>
          <strong style={{ fontSize: 14 }}>Check test cases</strong>
          <div className="muted" style={{ fontSize: 13 }}>
            Run a correct solution against every case to catch a wrong expected output
          </div>
        </div>
        <button onClick={() => { setOpen((v) => !v); if (!code) setCode(starterCode); }}>
          {open ? "Close" : "Open"}
        </button>
      </div>

      {open && (
        <>
          {dirty && (
            <div className="notice" style={{ marginTop: 10 }}>
              This runs against the <strong>saved</strong> test cases. Save first, or you will be
              checking the previous version.
            </div>
          )}
          <div style={{ height: 200, border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden", marginTop: 10 }}>
            <Editor
              language="c"
              theme="vs-dark"
              value={code}
              onChange={(v) => setCode(v ?? "")}
              options={{ fontSize: 13, minimap: { enabled: false }, tabSize: 4, automaticLayout: true }}
            />
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            <button className="primary" onClick={check} disabled={busy || !code.trim()}>
              {busy ? "Running…" : "Check test cases"}
            </button>
            <span className="muted" style={{ fontSize: 13 }}>
              Nothing is saved or graded — this solution is not stored.
            </span>
          </div>

          {error && <div className="error-banner" style={{ marginTop: 10 }}>{error}</div>}

          {report && !report.compiled && (
            <div className="error-banner" style={{ marginTop: 10 }}>
              <strong>Your solution did not compile.</strong>
              <pre style={{ whiteSpace: "pre-wrap", margin: "6px 0 0", fontSize: 12.5 }}>
                {report.compilerOutput}
              </pre>
            </div>
          )}

          {report?.compiled && (
            <div style={{ marginTop: 12 }}>
              <div className={disagreements.length === 0 ? "notice" : "error-banner"}>
                {disagreements.length === 0
                  ? `All ${report.results.length} test case${report.results.length === 1 ? "" : "s"} agree with this solution.`
                  : `${disagreements.length} of ${report.results.length} test cases disagree. Either the expected output is wrong, or this solution is.`}
              </div>
              <table>
                <thead>
                  <tr><th>Case</th><th>Result</th><th>Expected</th><th>Actual</th></tr>
                </thead>
                <tbody>
                  {report.results.map((r) => (
                    <tr key={r.testCaseId}>
                      <td>
                        {r.name}
                        {r.hidden && <span className="badge draft" style={{ marginLeft: 6 }}>hidden</span>}
                      </td>
                      <td>
                        <span className={`badge ${r.agrees ? "pass" : "fail"}`}>
                          {r.agrees ? "agrees" : "differs"}
                        </span>
                        {r.reason && !r.agrees && (
                          <div className="muted" style={{ fontSize: 12 }}>{r.reason}</div>
                        )}
                      </td>
                      <td><pre className="cell-pre">{r.expectedStdout || "(empty)"}</pre></td>
                      <td><pre className="cell-pre">{r.actualStdout || "(empty)"}</pre></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
